import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

const launchedCallSchema = z.object({
  callSid: z.string(),
  conversationId: z.string(),
});

/** Keep the retry key after an ambiguous failure so a second click cannot dial twice. */
export function useLaunchOutboundCall(conversationId: string) {
  const queryClient = useQueryClient();
  const requestIds = useRef(new Map<string, string>());
  const mutation = useMutation({
    mutationFn: (leadId: string) => {
      const requestId = requestIds.current.get(leadId) ?? crypto.randomUUID();
      requestIds.current.set(leadId, requestId);
      return apiClient.post(
        `voice-receptionist/leads/${leadId}/calls`,
        { requestId },
        { schema: launchedCallSchema }
      );
    },
    onSuccess: (call, leadId) => {
      requestIds.current.delete(leadId);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.voiceReceptionist.calls(conversationId),
      });
      if (call.callSid.startsWith('CAdryrun')) {
        toast.info('Call simulated. No phone was rung.');
      } else {
        toast.success('AI call started');
      }
    },
    onError: (error: Error) =>
      toast.error(error.message || 'Could not start the AI call'),
  });
  return {
    launchCall: mutation.mutate,
    isStarting: mutation.isPending,
    startedCallSid: mutation.data?.callSid ?? null,
  };
}
